import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { CartItem } from '@/lib/shopify';

// Re-export CartItem for consumers
export type { CartItem };

interface CartStore {
  items: CartItem[];
  cartId: string | null;
  checkoutUrl: string | null;
  isLoading: boolean;
  isSyncing: boolean;
  _hasHydrated: boolean;
  
  // Actions
  addItem: (item: CartItem) => void;
  updateQuantity: (variantId: string, quantity: number) => void;
  removeItem: (variantId: string) => void;
  clearCart: () => void;
  setCartId: (cartId: string) => void;
  setCheckoutUrl: (url: string) => void;
  setLoading: (loading: boolean) => void;
  createCheckout: () => Promise<void>;
  syncCart: () => Promise<void>;
  syncShopifyCartStatus: () => Promise<void>;
  setHasHydrated: (state: boolean) => void;
}

export const useCartStore = create<CartStore>()(
  persist(
    function(set, get) {
      return {
        items: [],
        cartId: null,
        checkoutUrl: null,
        isLoading: false,
        isSyncing: false,
        _hasHydrated: false,

        setHasHydrated: function(state) { set({ _hasHydrated: state }); },

        addItem: function(item) {
          const items = get().items;
          const existingItem = items.find(function(i) { return i.variantId === item.variantId; });
          
          if (existingItem) {
            set({
              items: items.map(function(i) {
                return i.variantId === item.variantId
                  ? { ...i, quantity: i.quantity + item.quantity }
                  : i;
              })
            });
          } else {
            set({ items: [...items, item] });
          }
        },

        updateQuantity: function(variantId, quantity) {
          if (quantity <= 0) {
            get().removeItem(variantId);
            return;
          }
          
          set({
            items: get().items.map(function(item) {
              return item.variantId === variantId ? { ...item, quantity: quantity } : item;
            })
          });
        },

        removeItem: function(variantId) {
          set({
            items: get().items.filter(function(item) { return item.variantId !== variantId; })
          });
        },

        clearCart: function() {
          console.log('Clearing regular cart');
          set({ items: [], cartId: null, checkoutUrl: null });
        },

        setCartId: function(cartId) { set({ cartId: cartId }); },
        setCheckoutUrl: function(checkoutUrl) { set({ checkoutUrl: checkoutUrl }); },
        setLoading: function(isLoading) { set({ isLoading: isLoading }); },

        createCheckout: async function() {
          const state = get();
          if (state.items.length === 0) return;

          state.setLoading(true);
          try {
            // Dynamic import to avoid module-level execution issues
            const { createStorefrontCheckout } = await import('@/lib/shopify');
            const result = await createStorefrontCheckout(state.items);
            state.setCheckoutUrl(result.checkoutUrl);
            state.setCartId(result.cartId);
            console.log('Cart created with ID:', result.cartId);
          } catch (error) {
            console.error('Failed to create checkout:', error);
            throw error;
          } finally {
            state.setLoading(false);
          }
        },

        // Alias for syncShopifyCartStatus
        syncCart: async function() {
          return get().syncShopifyCartStatus();
        },

        // Check if the Shopify cart still exists (used after returning from checkout)
        syncShopifyCartStatus: async function() {
          const state = get();
          
          // Wait for hydration before syncing
          if (!state._hasHydrated) {
            console.log('Store not yet hydrated, skipping sync');
            return;
          }
          
          if (!state.cartId) {
            console.log('No cart ID to sync');
            return;
          }
          
          if (state.isSyncing) {
            console.log('Already syncing, skipping');
            return;
          }

          set({ isSyncing: true });
          try {
            console.log('Checking Shopify cart status for ID:', state.cartId);
            // Dynamic import to avoid module-level execution issues
            const { checkShopifyCart } = await import('@/lib/shopify');
            const result = await checkShopifyCart(state.cartId);
            
            console.log('Shopify cart check result - exists:', result.exists, 'hasItems:', result.hasItems);
            if (!result.exists || !result.hasItems) {
              console.log('Cart checkout completed, clearing local cart');
              state.clearCart();
            }
          } catch (error) {
            console.error('Failed to sync cart status:', error);
          } finally {
            set({ isSyncing: false });
          }
        }
      };
    },
    {
      name: 'shopify-cart',
      storage: createJSONStorage(function() { return localStorage; }),
      partialize: function(state) {
        return { 
          items: state.items, 
          cartId: state.cartId, 
          checkoutUrl: state.checkoutUrl 
        };
      },
      onRehydrateStorage: function() {
        return function(state) {
          console.log('Cart rehydrated from localStorage:', state?.items?.length || 0, 'items');
          if (state) state.setHasHydrated(true);
        };
      },
    }
  )
);
